import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { AlertComponent } from "@modules/core/components/alert/alert.component";
import { createDialogConfig } from "@utils";
import { Observable } from "rxjs";
import { G2RatingResultModel, G2ReviewRating } from "types";

export interface G2ReviewPromptDialogData {
  /** Sends the rating. The prompt stays open until it succeeds. */
  submitRating: (rating: G2ReviewRating) => Observable<G2RatingResultModel>;
}

/**
 * `accepted` when the rating was stored. `rejected` when the server refused it
 * (409: no recorded prompt), so neither the G2 form nor feedback should open.
 * `undefined` when the prompt was dismissed without a rating.
 */
export type G2ReviewPromptDialogResult =
  | { outcome: "accepted"; rating: G2ReviewRating; reviewUrl?: string | null }
  | { outcome: "rejected"; rating: G2ReviewRating }
  | undefined;

@Component({
  selector: "app-g2-review-prompt-dialog",
  templateUrl: "./g2-review-prompt-dialog.component.html",
  styleUrls: ["./g2-review-prompt-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class G2ReviewPromptDialogComponent {
  public isSubmitting = false;

  public constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: G2ReviewPromptDialogData,
    private matDialogRef: MatDialogRef<
      G2ReviewPromptDialogComponent,
      G2ReviewPromptDialogResult
    >,
    private matDialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  public static openDialog(
    matDialog: MatDialog,
    data: G2ReviewPromptDialogData
  ): MatDialogRef<G2ReviewPromptDialogComponent, G2ReviewPromptDialogResult> {
    const config = createDialogConfig<G2ReviewPromptDialogData>(data, {
      panelClasses: ["g2-review-prompt-dialog"],
      minWidth: "340px",
      maxWidth: "95vw",
    });
    config.autoFocus = "dialog";
    return matDialog.open<
      G2ReviewPromptDialogComponent,
      G2ReviewPromptDialogData,
      G2ReviewPromptDialogResult
    >(G2ReviewPromptDialogComponent, config);
  }

  public select(rating: G2ReviewRating): void {
    if (this.isSubmitting) return;
    this.setSubmitting(true);

    this.dialogData.submitRating(rating).subscribe({
      next: (result) =>
        this.matDialogRef.close({
          outcome: "accepted",
          rating,
          reviewUrl: result.reviewUrl,
        }),
      error: (error: unknown) => {
        AlertComponent.openErrorDialogFromErrorDescriptor(this.matDialog);
        if (error instanceof HttpErrorResponse && error.status === 409) {
          this.matDialogRef.close({ outcome: "rejected", rating });
          return;
        }
        this.setSubmitting(false);
      },
    });
  }

  private setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    // Closing mid-request would drop a rating the server may have stored.
    this.matDialogRef.disableClose = isSubmitting;
    this.changeDetectorRef.markForCheck();
  }
}
